import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./style.css";

const SOURCES = [
  { id: "tiktok",    label: "TikTok",        emoji: "🎵" },
  { id: "instagram", label: "Instagram",     emoji: "📸" },
  { id: "reddit",    label: "Reddit",        emoji: "🤖" },
  { id: "google",    label: "Google",        emoji: "🔍" },
  { id: "linkedin",  label: "LinkedIn",      emoji: "💼" },
  { id: "friend",    label: "Friend",        emoji: "🙌" },
  { id: "other",     label: "Other",         emoji: "✨" },
];

const JOBS = [
  { label: "Nursing",             emoji: "🩺" },
  { label: "Flight Attendant",    emoji: "✈️" },
  { label: "Marketing",           emoji: "📣" },
  { label: "Assistant",           emoji: "📋" },
  { label: "SWE",                 emoji: "💻" },
  { label: "Sales",               emoji: "📞" },
  { label: "Cabin Crew",          emoji: "🧳" },
  { label: "PM",                  emoji: "🧩" },
  { label: "Solutions Architect", emoji: "🏗️" },
  { label: "Legal",               emoji: "⚖️" },
  { label: "Healthcare",          emoji: "🏥" },
  { label: "Finance",             emoji: "💰" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const pad = (n) => String(n).padStart(2, "0");
// Build the date string manually so we never get bitten by toISOString() UTC shifts.
const fmt = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const Onboarding = () => {
  const navigate = useNavigate();

  const isReferred = localStorage.getItem("userReferred") === "true";

  const [step, setStep] = useState(isReferred ? 2 : 1);
  const [saving, setSaving] = useState(false);

  // ── Step 1: source ──
  const [source, setSource] = useState(isReferred ? "Friend Referral" : "");
  const [otherText, setOtherText] = useState("");

  // ── Step 2: interview ──
  const [hasInterview, setHasInterview] = useState(null); // null | true (shows calendar)
  const [pickedDate, setPickedDate] = useState(null);     // "YYYY-MM-DD" | null
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selected, setSelected] = useState(null); // { y, m, d }

  // ── Step 3: job ──
  const [job, setJob] = useState("");          // selected preset label
  const [addingOwn, setAddingOwn] = useState(false);
  const [customRole, setCustomRole] = useState("");

  // Submit everything to the backend, then enter the app.
  const finalize = async (role) => {
    setSaving(true);
    localStorage.setItem("desiredRole", role || "");
    const finalSource = source === "other" ? `other: ${otherText}` : source;
    try {
      await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/user/onboarding`,
        {
          source: finalSource,
          email: localStorage.getItem("userEmail"),
          hasInterview: !!pickedDate,
          interviewDate: pickedDate, // "YYYY-MM-DD" or null
          desiredRole: role || null,
        },
      );
    } catch (_) {
      // fail silently
    } finally {
      setSaving(false);
      navigate("/mockInterviewerInput");
    }
  };

  // ── Step 1 handlers ──
  const handleSelectSource = (id) => {
    if (id === "other") {
      setSource("other");
      return; // wait for text input + continue
    }
    setSource(id);
    setStep(2);
  };
  const otherReady = source === "other" && otherText.trim() !== "";

  // ── Step 2 handlers ──
  const goToJobs = (dateStr) => {
    setPickedDate(dateStr);
    setStep(3);
  };

  const atCurrentMonth =
    calYear === today.getFullYear() && calMonth === today.getMonth();
  const prevMonth = () => {
    if (atCurrentMonth) return; // don't page into fully-past months
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else {
      setCalMonth((m) => m - 1);
    }
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else {
      setCalMonth((m) => m + 1);
    }
  };

  const firstWeekday = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isPast = (d) => new Date(calYear, calMonth, d) < today;
  const isToday = (d) =>
    calYear === today.getFullYear() &&
    calMonth === today.getMonth() &&
    d === today.getDate();
  const isSelected = (d) =>
    selected && selected.y === calYear && selected.m === calMonth && selected.d === d;

  // ── Step 3 handlers ──
  const handleSelectJob = (label) => {
    setAddingOwn(false);
    setCustomRole("");
    setJob(label);
  };
  const handleAddOwn = () => {
    setJob("");
    setAddingOwn(true);
  };
  const customReady = customRole.trim() !== "";

  return (
    <div className="ob-root">
      <div className="ob-blob ob-blob-1" />
      <div className="ob-blob ob-blob-2" />

      <div className={`ob-card ${step === 3 ? "ob-card-wide" : ""}`}>
        <div className="ob-steps">
          <span className={`ob-step-dot ${step >= 1 ? "ob-step-dot-active" : ""}`} />
          <span className={`ob-step-dot ${step >= 2 ? "ob-step-dot-active" : ""}`} />
          <span className={`ob-step-dot ${step >= 3 ? "ob-step-dot-active" : ""}`} />
        </div>

        {/* ───────── STEP 1: source ───────── */}
        {step === 1 && (
          <>
            <h2 className="ob-title">How did you find out about Nora?</h2>
            <p className="ob-sub">This helps us understand how users find us!</p>

            <div className="ob-list">
              {SOURCES.map((s) => (
                <button
                  key={s.id}
                  className={`ob-row ${source === s.id ? "ob-row-selected" : ""}`}
                  onClick={() => handleSelectSource(s.id)}
                  disabled={saving}
                  type="button"
                >
                  <span className="ob-row-emoji">{s.emoji}</span>
                  <span className="ob-row-label">{s.label}</span>
                </button>
              ))}
            </div>

            {source === "other" && (
              <div className="ob-other-wrap">
                <input
                  className="ob-other-input"
                  type="text"
                  placeholder="Where did you find us?"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && otherReady && setStep(2)}
                  autoFocus
                />
                <button
                  className={`ob-continue-btn ${otherReady ? "ob-continue-active" : ""}`}
                  onClick={() => setStep(2)}
                  disabled={!otherReady || saving}
                  type="button"
                >
                  Continue
                </button>
              </div>
            )}

            <button
              className="ob-skip-btn"
              onClick={() => {
                setSource("");
                setStep(2);
              }}
              type="button"
            >
              Skip Question
            </button>
          </>
        )}

        {/* ───────── STEP 2a: do you have an interview? ───────── */}
        {step === 2 && hasInterview !== true && (
          <>
            <h2 className="ob-title">Do you have an interview coming up?</h2>
            <p className="ob-sub">
              Quick context — helps us pace your practice and set the right urgency.
            </p>

            <div className="ob-list">
              <button
                className="ob-choice"
                onClick={() => setHasInterview(true)}
                disabled={saving}
                type="button"
              >
                <span className="ob-choice-title">Yes — it's on the calendar</span>
                <span className="ob-choice-sub">We'll tune everything around your dates.</span>
              </button>
              <button
                className="ob-choice"
                onClick={() => goToJobs(null)}
                disabled={saving}
                type="button"
              >
                <span className="ob-choice-title">Not yet — preparing ahead</span>
                <span className="ob-choice-sub">Smart. Start early, walk in ready.</span>
              </button>
            </div>
          </>
        )}

        {/* ───────── STEP 2b: pick the interview date ───────── */}
        {step === 2 && hasInterview === true && (
          <>
            <h2 className="ob-title">When is your interview?</h2>
            <p className="ob-sub">We'll pace your prep accordingly.</p>

            <div className="ob-cal">
              <div className="ob-cal-head">
                <span className="ob-cal-month">
                  {MONTHS[calMonth]} {calYear}
                </span>
                <div className="ob-cal-nav">
                  <button
                    className="ob-cal-arrow"
                    onClick={prevMonth}
                    disabled={atCurrentMonth}
                    type="button"
                    aria-label="Previous month"
                  >
                    ‹
                  </button>
                  <button
                    className="ob-cal-arrow"
                    onClick={nextMonth}
                    type="button"
                    aria-label="Next month"
                  >
                    ›
                  </button>
                </div>
              </div>

              <div className="ob-cal-grid">
                {DOW.map((d) => (
                  <div key={d} className="ob-cal-dow">
                    {d}
                  </div>
                ))}
                {cells.map((d, i) => {
                  if (d === null)
                    return <div key={`e${i}`} className="ob-cal-day ob-cal-day-empty" />;
                  const past = isPast(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      disabled={past || saving}
                      onClick={() => setSelected({ y: calYear, m: calMonth, d })}
                      className={`ob-cal-day ${past ? "ob-cal-day-past" : ""} ${
                        isToday(d) ? "ob-cal-day-today" : ""
                      } ${isSelected(d) ? "ob-cal-day-selected" : ""}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              className={`ob-continue-btn ${selected ? "ob-continue-active" : ""}`}
              onClick={() => selected && goToJobs(fmt(selected.y, selected.m, selected.d))}
              disabled={!selected || saving}
              type="button"
            >
              Continue
            </button>

            <button
              className="ob-noint-link"
              onClick={() => goToJobs(null)}
              disabled={saving}
              type="button"
            >
              I don't have an interview yet
            </button>
          </>
        )}

        {/* ───────── STEP 3: job ───────── */}
        {step === 3 && (
          <>
            <h2 className="ob-title">What kind of job are you looking for?</h2>
            <p className="ob-sub">
              We stock our library with questions, rubrics and interviewer personas for your exact role.
            </p>

            <div className="ob-jobs">
              {JOBS.map((j) => (
                <button
                  key={j.label}
                  className={`ob-job ${job === j.label && !addingOwn ? "ob-job-selected" : ""}`}
                  onClick={() => handleSelectJob(j.label)}
                  disabled={saving}
                  type="button"
                >
                  <span className="ob-job-emoji">{j.emoji}</span>
                  <span className="ob-job-label">{j.label}</span>
                </button>
              ))}

              <button
                className={`ob-job ob-job-add ${addingOwn ? "ob-job-add-active" : ""}`}
                onClick={handleAddOwn}
                disabled={saving}
                type="button"
              >
                <span className="ob-job-plus">+</span>
                <span className="ob-job-label">Add your own</span>
              </button>
            </div>

            {addingOwn && (
              <div className="ob-role-box">
                <span className="ob-role-label">Tell us your role</span>
                <div className="ob-role-row">
                  <input
                    className="ob-role-input"
                    type="text"
                    placeholder="e.g. Data Science, Product Design, Nursing..."
                    value={customRole}
                    onChange={(e) => setCustomRole(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && customReady && finalize(customRole.trim())
                    }
                    autoFocus
                  />
                  <button
                    className={`ob-role-continue ${customReady ? "ob-continue-active" : ""}`}
                    onClick={() => finalize(customRole.trim())}
                    disabled={!customReady || saving}
                    type="button"
                  >
                    Continue →
                  </button>
                </div>
              </div>
            )}

            {job && !addingOwn && (
              <button
                className="ob-continue-btn ob-continue-active"
                onClick={() => finalize(job)}
                disabled={saving}
                type="button"
              >
                Continue
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Onboarding;